import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Settings as SettingsIcon, HardDrive, Link2, Building2, Camera, FileText, Bell, Sparkles, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';

const Settings = () => {
  const navigate = useNavigate();

  const settingsCategories = [
    {
      title: 'User Management',
      description: 'Manage users, roles & access',
      icon: 'users',
      path: '/settings/users',
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Agent Installer',
      description: 'Upload & manage RMM agents',
      icon: HardDrive,
      path: '/settings/agent-installer',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Integrations',
      description: 'SMTP, ScreenConnect & more',
      icon: Link2,
      path: '/settings/integrations',
      gradient: 'from-green-500 to-green-600'
    },
    {
      title: 'Client Configuration',
      description: 'Contract terms & job titles',
      icon: Building2,
      path: '/settings/client-configuration',
      gradient: 'from-orange-500 to-orange-600'
    },
    {
      title: 'Screenshot Settings',
      description: 'Configure screenshot recording',
      icon: Camera,
      path: '/settings/screenshots',
      gradient: 'from-cyan-500 to-cyan-600'
    },
    {
      title: 'Asset Alerts',
      description: 'Global alert thresholds & rules',
      icon: Bell,
      path: '/settings/asset-alerts',
      gradient: 'from-red-500 to-red-600'
    },
    {
      title: 'AI Settings',
      description: 'Language, tone & AI preferences',
      icon: Sparkles,
      path: '/settings/ai',
      gradient: 'from-pink-500 to-pink-600'
    },
    {
      title: 'Regional Settings',
      description: 'Date format, timezone & localization',
      icon: SettingsIcon,
      path: '/settings/regional',
      gradient: 'from-teal-500 to-teal-600'
    },
    {
      title: 'Development Resources',
      description: 'Changelog & roadmap tracking',
      icon: FileText,
      path: '/settings/development-resources',
      gradient: 'from-indigo-500 to-indigo-600'
    },
    {
      title: 'Pricing & Quoting',
      description: 'Configure services & pricing for quotes',
      icon: DollarSign,
      path: '/settings/pricing',
      gradient: 'from-emerald-500 to-emerald-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="border-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                <SettingsIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Settings</h1>
                <p className="text-sm text-slate-500">Configure your system preferences</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {settingsCategories.map((category, index) => (
            <Card
              key={index}
              className="shadow-sm border-slate-200 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105"
              onClick={() => navigate(category.path)}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${category.gradient} rounded-xl flex items-center justify-center shadow-md`}>
                    {category.icon === 'users' ? (
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    ) : (
                      <category.icon className="w-7 h-7 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800 text-lg">{category.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{category.description}</p>
                  </div>
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Settings;
